import { useMemo, useState } from "react";
import { useT } from "@/lib/i18n";
import type { ColumnDef } from "@tanstack/react-table";
import { ScrollText } from "lucide-react";
import { AuditEvent } from "@gen/tracker/v1/audit_pb";
import { ResourceKind } from "@gen/tracker/v1/common_pb";
import { ListView } from "@/components/list-view";
import {
  DiffViewer,
  PageHeader,
  SearchField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@fox1ck-org/ui";
import { PageState } from "@fox1ck-org/ui";
import { failureText } from "@/lib/errors";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { usePeopleNames } from "@/lib/services/workspace-service";
import {
  actionLabel,
  actionTone,
  parseDiff,
  resourceKindLabel,
  resourceKindOptions,
  useSearchAuditEvents,
} from "@/lib/services/audit-service";

const PAGE_SIZES = [50, 100, 250];

const ALL = "__all__";

export default function LogsPage() {
  const params = useTableParams({ defaultPageSize: 50, pageSizeOptions: PAGE_SIZES });
  const actorId = params.getFilter("actor");
  const action = params.getFilter("action");
  const kindRaw = params.getFilter("kind");
  const resourceKind = kindRaw ? (Number(kindRaw) as ResourceKind) : undefined;

  const { data, isLoading, error, refetch } = useSearchAuditEvents({
    range: { from: params.from, to: params.to },
    actorId: actorId || undefined,
    action: action || undefined,
    resourceKind,
    limit: params.pageSize,
    offset: params.pageIndex * params.pageSize,
  });
  const items = data?.items ?? [];
  const tr = useT();
  const people = usePeopleNames([...items.map((e) => e.actorId)]);
  const names = people.data ?? {};

  const [selected, setSelected] = useState<AuditEvent | null>(null);

  // SearchAuditEventsRequest takes a single resource_kind. This used to be a
  // multi-select FilterBar taught to behave radio-like by picking "the value
  // that is not the current one" out of the array it was handed; a Select is
  // what one-of-many actually is.
  const kindOptions = useMemo(() => resourceKindOptions(), []);

  const columns = useMemo<ColumnDef<AuditEvent>[]>(
    () => [
      {
        id: "ts",
        header: tr("logs.col.time"),
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-xs tabular-nums text-muted-foreground">
            {row.original.ts || "—"}
          </span>
        ),
        size: 170,
      },
      {
        id: "actor",
        header: tr("logs.col.actor"),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-xs text-foreground">
              {names[row.original.actorId] || row.original.actorEmail || row.original.actorId || tr("logs.system")}
            </span>
            {row.original.actorId ? (
              <span className="font-mono text-xs text-muted-foreground">
                {row.original.actorId}
              </span>
            ) : null}
          </div>
        ),
        size: 220,
      },
      {
        id: "action",
        header: tr("logs.col.action"),
        enableSorting: false,
        cell: ({ row }) => (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
              actionTone(row.original.action),
            )}
          >
            {actionLabel(row.original.action) || "—"}
          </span>
        ),
        size: 170,
      },
      {
        id: "resource",
        header: tr("logs.col.resource"),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-xs text-foreground">
              {resourceKindLabel(row.original.resourceKind)}
            </span>
            <span className="break-all font-mono text-xs text-muted-foreground">
              {row.original.resourceId || "—"}
            </span>
          </div>
        ),
      },
      {
        id: "ip",
        header: "IP",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-xs tabular-nums text-muted-foreground">
            {row.original.ip || "—"}
          </span>
        ),
        size: 130,
      },
    ],
    [tr, names],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-col gap-3 px-5 pb-3 pt-4">
        <PageHeader
          icon={<ScrollText className="size-4" strokeWidth={1.75} />}
          title={tr("logs.title")}
          subtitle={tr("logs.subtitle")}
        />
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 px-5 pb-5 lg:grid-cols-[1fr_--spacing(95)]">
        <div className="flex min-h-0 min-w-0 flex-col gap-3">
          {error ? (
            <PageState
              mode="error"
              title={tr("logs.error")}
              error={failureText(error)}
              retryLabel={tr("lib.common.retry")}
              retry={() => refetch()}
            />
          ) : null}

          <ListView
            columns={columns}
            data={items}
            loading={isLoading}
            manualSorting
            toolbarLeft={
              <>
                <Select
                  value={kindRaw || ALL}
                  onValueChange={(v) => params.setFilter("kind", v === ALL ? null : v)}
                >
                  <SelectTrigger className="h-8 w-44" aria-label={tr("logs.col.resource")}>
                    <SelectValue placeholder={tr("logs.allResources")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>
                      <span className="text-muted-foreground">{tr("logs.allResources")}</span>
                    </SelectItem>
                    {kindOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <SearchField
                  value={actorId}
                  onChange={(v) => params.setFilter("actor", v || null)}
                  debounceMs={350}
                  label={tr("logs.filter.actor")}
                  placeholder={tr("logs.filter.actor.ph")}
                  className="w-40"
                />
                <SearchField
                  value={action}
                  onChange={(v) => params.setFilter("action", v || null)}
                  debounceMs={350}
                  label={tr("logs.filter.action")}
                  placeholder={tr("logs.filter.action.ph")}
                  className="w-52"
                />
              </>
            }
            pagination={{
              pageIndex: params.pageIndex,
              pageSize: params.pageSize,
              totalCount: data?.total ?? 0,
              onPageIndexChange: params.setPageIndex,
              onPageSizeChange: params.setPageSize,
              pageSizeOptions: PAGE_SIZES,
            }}
            onRowClick={setSelected}
            emptyMessage={tr("logs.empty")}
          />
        </div>

        <aside className="h-fit overflow-y-auto rounded-lg border border-border bg-card p-4 shadow-[var(--elev-1)]">
          <div className="text-label text-muted-foreground">{tr("logs.detail")}</div>
          {selected ? (
            <EventDetail event={selected} actorName={names[selected.actorId]} />
          ) : (
            <div className="mt-3 text-xs text-muted-foreground">
              {tr("logs.detail.hint")}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function EventDetail({ event, actorName }: { event: AuditEvent; actorName?: string }) {
  const tr = useT();
  const diff = parseDiff(event.beforeJson, event.afterJson);
  return (
    <div className="mt-3 space-y-3">
      <div>
        <div className="text-sm font-semibold text-foreground">
          {resourceKindLabel(event.resourceKind)}
        </div>
        <div className="break-all font-mono text-xs text-muted-foreground">
          {event.resourceId || "—"}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 font-semibold uppercase tracking-wide",
            actionTone(event.action),
          )}
        >
          {actionLabel(event.action) || "—"}
        </span>
        <span className="tabular-nums text-muted-foreground">{event.ts}</span>
      </div>
      <div className="text-xs">
        <div className="text-muted-foreground">{tr("logs.by")}</div>
        <div className="text-foreground">
          {actorName || event.actorEmail || event.actorId || tr("logs.system")}
          {event.actorId ? (
            <span className="ml-1 font-mono text-xs text-muted-foreground">
              ({event.actorId})
            </span>
          ) : null}
        </div>
      </div>

      {diff ? (
        <div className="space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {tr("logs.changes")}
          </div>
          <DiffViewer left={diff.before ?? {}} right={diff.after ?? {}} />
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">{tr("logs.noDiff")}</div>
      )}

      <dl className="divide-y divide-border rounded-lg border border-border bg-muted/20">
        <MetaRow label={tr("logs.requestId")} value={event.requestId} mono />
        <MetaRow label="IP" value={event.ip} mono />
        <MetaRow label={tr("logs.ua")} value={event.userAgent} mono small />
      </dl>
    </div>
  );
}

function MetaRow({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value: string;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div className="grid grid-cols-[--spacing(22.5)_1fr] items-start gap-2 px-2.5 py-1.5 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "min-w-0 break-all text-foreground",
          mono && "font-mono",
          small && "text-xs text-muted-foreground",
        )}
      >
        {value || "—"}
      </dd>
    </div>
  );
}
